import axios from "axios"

/**
 * Transcript texts by URL. The shell loads every track's transcripts once, up front: a toggle
 * later in the lesson never needs a (by then possibly expired) signed transcript URL. A failed
 * load or a body that is not WebVTT counts as "no transcript" and is not retried.
 */
export function createTranscriptStore({ load }) {
    const texts = new Map()
    const pending = new Map()

    function ensure(url) {
        if (texts.has(url)) {
            return Promise.resolve(texts.get(url))
        }

        if (!pending.has(url)) {
            let request

            // Called synchronously, so every load has started when loadFor() returns.
            try {
                request = Promise.resolve(load(url))
            } catch {
                request = Promise.resolve(null)
            }

            pending.set(
                url,
                request
                    .catch(() => null)
                    .then((text) => {
                        const value = typeof text === "string" && text.includes("-->") ? text : null
                        texts.set(url, value)
                        pending.delete(url)

                        return value
                    }),
            )
        }

        return pending.get(url)
    }

    return {
        ensure,
        loadFor(tracks) {
            const urls = [...new Set(tracks.flatMap((track) => track.transcripts.map((transcript) => transcript.url)))]

            return Promise.all(urls.map(ensure))
        },
        textsFor(track) {
            return track.transcripts
                .map((transcript) => ({ ...transcript, text: texts.get(transcript.url) ?? null }))
                .filter((transcript) => transcript.text !== null)
        },
    }
}

export async function defaultTranscriptLoader(url) {
    try {
        const response = await axios.get(url)

        return typeof response.data === "string" ? response.data : null
    } catch (error) {
        console.warn("[PlayerShell] transcript failed to load", error?.message ?? error)

        return null
    }
}
